import { onMessage } from 'firebase/messaging'
import { messaging, debugFirebaseStatus } from 'utils/firebase'
import { rootStore } from 'stores'
import { fcmTokenManager } from './fcmTokenManager.service'

export interface IFirebaseNotificationPayload {
  type: 'message' | 'notification' | 'support_message' | 'workplan_lock_forced' | 'process_lock_forced'
  threadId?: number
  messageId?: number
  userId?: number
  organizationId?: number
  title?: string
  body?: string
  timestamp?: number
  workplanId?: number
  processId?: number
  newOwnerName?: string
}

class FirebaseMessagingService {
  private isListening = false

  public async startListening(): Promise<void> {
    if (this.isListening) return

    const { authStore } = rootStore
    const userId = authStore.userDetail?.id

    if (!userId) {
      console.warn('Cannot start Firebase messaging: User not authenticated')
      return
    }

    // Debug Firebase status
    const firebaseStatus = debugFirebaseStatus()

    // Check if Firebase and messaging are properly configured
    if (!messaging || !firebaseStatus.isInitialized) {
      console.error('Cannot start Firebase messaging - messaging object is null or Firebase not initialized')
      return
    }

    console.log('Starting Firebase messaging listener...')

    // Initialize FCM token if not already done
    await fcmTokenManager.initializeToken(userId)

    this.isListening = true
    console.log('Firebase messaging listener started')

    onMessage(messaging, payload => {
      // Check if payload has data and required type field
      if (!payload.data || !payload.data.type) {
        console.warn('Firebase message received without valid data:', payload)
        return
      }

      // Validate message type
      const validTypes = ['message', 'notification', 'support_message', 'workplan_lock_forced', 'process_lock_forced']
      if (!validTypes.includes(payload.data.type)) {
        console.warn('Firebase message received with invalid type:', payload.data.type)
        return
      }

      // Firebase data comes as strings, so we need to safely convert
      const data: IFirebaseNotificationPayload = {
        type: payload.data.type as 'message' | 'notification' | 'support_message' | 'workplan_lock_forced' | 'process_lock_forced',
        threadId: payload.data.threadId ? parseInt(payload.data.threadId, 10) : undefined,
        messageId: payload.data.messageId ? parseInt(payload.data.messageId, 10) : undefined,
        userId: payload.data.userId ? parseInt(payload.data.userId, 10) : undefined,
        organizationId: payload.data.organizationId ? parseInt(payload.data.organizationId, 10) : undefined,
        title: payload.data.title || payload.notification?.title,
        body: payload.data.body || payload.notification?.body,
        timestamp: payload.data.timestamp ? parseInt(payload.data.timestamp, 10) : undefined,
        workplanId: payload.data.workplanId && payload.data.workplanId !== '' ? parseInt(payload.data.workplanId, 10) : undefined,
        processId: payload.data.processId && payload.data.processId !== '' ? parseInt(payload.data.processId, 10) : undefined,
        newOwnerName: payload.data.newOwnerName && payload.data.newOwnerName !== '' ? payload.data.newOwnerName : undefined
      }

      console.log('Processing Firebase message:', data)

      // Handle different types of Firebase messages
      switch (data.type) {
        case 'message':
          this.handleGeneralMessage(data)
          break
        case 'support_message':
          this.handleSupportMessage(data)
          break
        case 'notification':
          this.handleNotification(data)
          break
        case 'workplan_lock_forced':
          this.handleWorkplanLockForced(data)
          // Don't show browser notification for lock forced - it will show modal instead
          return
        case 'process_lock_forced':
          this.handleProcessLockForced(data)
          return
        default:
          console.log('Unknown Firebase message type:', data.type)
      }

      // Show browser notification if tab is active (optional)
      // Note: workplan_lock_forced already returns above, so it won't reach here
      if (document.visibilityState === 'visible') {
        this.showBrowserNotification(payload, data)
      }
    })
  }

  public stopListening(): void {
    this.isListening = false
    console.log('Firebase messaging listener stopped')
  }

  public getStatus(): { isListening: boolean; hasMessaging: boolean } {
    return {
      isListening: this.isListening,
      hasMessaging: !!messaging
    }
  }

  private handleGeneralMessage(data: IFirebaseNotificationPayload): void {
    const { messageStore } = rootStore

    console.log('Handling general message Firebase update:', data)

    const currentPath = window.location.pathname
    const isInMessagesPage = currentPath.includes('/messages')

    // Always refresh data for real-time updates (both sender and receiver need to see the message)
    messageStore.fetchGeneralMessageThreads(20, messageStore.generaltMessageKeyword, false)
    messageStore.getUnreadThread()

    // If we're currently viewing the thread that received a new message, refresh messages
    if (data.threadId === messageStore.currentGeneralThreadId && isInMessagesPage) {
      messageStore.getMessages()
    }
  }

  private handleSupportMessage(data: IFirebaseNotificationPayload): void {
    const { messageStore, organizationStore } = rootStore
    const organizationId = organizationStore.organization?.id || 0

    console.log('Handling support message Firebase update:', data)

    const currentPath = window.location.pathname
    const isInMessagesPage = currentPath.includes('/messages')

    // Always refresh data for real-time updates (all participants need to see the message)
    messageStore.getSupportMessageThreads(organizationId, 20, false)
    messageStore.getUnreadThread()

    // If we're currently viewing the thread that received a new message, refresh messages
    if (data.threadId === messageStore.currentSupportThreadId && isInMessagesPage) {
      messageStore.getSupportMessages()
      messageStore.getThreadDetail()
    }
  }

  private handleNotification(data: IFirebaseNotificationPayload): void {
    const { notificationStore, authStore } = rootStore
    const currentUserId = authStore.userDetail?.id

    console.log('Handling notification Firebase update:', data)

    // Only refresh if the notification is for the current user
    if (data.userId === currentUserId || !data.userId) {
      // Refresh notifications
      notificationStore.fetchNotifications()
      notificationStore.aggregateCountNotifications()
    }
  }

  private handleWorkplanLockForced(data: IFirebaseNotificationPayload): void {

    if (!data.workplanId) {
      console.warn('Workplan lock forced message missing workplanId')
      return
    }

    // Emit custom event for useEditLock hook to listen
    const event = new CustomEvent('workplan_lock_forced', {
      detail: {
        workplanId: data.workplanId,
        newOwnerName: data.newOwnerName || 'An administrator'
      }
    })
    window.dispatchEvent(event)
  }

  private handleProcessLockForced(data: IFirebaseNotificationPayload): void {
    if (!data.processId) {
      console.warn('Process lock forced message missing processId')
      return
    }

    const event = new CustomEvent('process_lock_forced', {
      detail: {
        processId: data.processId,
        newOwnerName: data.newOwnerName || 'An administrator'
      }
    })
    window.dispatchEvent(event)
  }

  private showBrowserNotification(payload: any, data?: IFirebaseNotificationPayload): void {
    if (Notification.permission === 'granted') {
     
    const currentPath = window.location.pathname
      if (currentPath.includes('/messages')) {
        return
      }

      const notificationTitle = data?.title || payload?.notification?.title || 'New Message!'
      const messageIdTag = data?.messageId ? `msg_${data.messageId}` : `msg_${Date.now()}`
      const threadTag = data?.type && data?.threadId ? `${data.type}_${data.threadId}` : 'howfactory-notification'
      const uniqueTag = `${threadTag}_${messageIdTag}`
      const notificationOptions = {
        body: data?.body || payload?.notification?.body || 'You have a new notification',
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        data: data || payload.data,
        tag: uniqueTag,
        renotify: true,
        requireInteraction: false,
        silent: false,
        actions: [
          {
            action: 'view',
            title: 'View',
            icon: '/favicon.ico'
          },
          {
            action: 'dismiss',
            title: 'Dismiss',
            icon: '/favicon.ico'
          }
        ]
      }

      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistration('/')
          .then(reg => {
            if (reg && 'showNotification' in reg) {
              reg.showNotification(notificationTitle, notificationOptions as NotificationOptions)
            }
          })
      }
    }
  }
}

export const firebaseMessagingService = new FirebaseMessagingService()
